// Boot: sizes the pixel canvas, runs the frame loop and switches between menus and the game.

import { CLASSES, SKILLS } from '../shared/classes';
import { C2S, S2C } from '../shared/protocol';
import { REALMS } from '../shared/realms';
import { MONSTERS } from '../shared/monsters';
import { analytics } from './analytics';
import { audio } from './audio';
import { heroFrame, monsterSprite } from './gfx/sprites';
import { Controls } from './game/controls';
import { Hud } from './game/hud';
import { WorldRenderer } from './game/render';
import { GameState } from './game/state';
import { input } from './input';
import { api, GameSocket, HeroSummary } from './net';
import { ScreenHost, Screens } from './screens';
import { C, ui } from './ui';
import { VoiceClient } from './voice';

const BASE_W = 480;
const BASE_H = 270;
const MIN_W = 420;
const MIN_H = 236;

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d', { alpha: false })!;

let scale = 1;
let W = BASE_W;
let H = BASE_H;

function resize(): void {
  const devW = window.innerWidth;
  const devH = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  const physW = devW * dpr;
  const physH = devH * dpr;
  // Whole-number scaling keeps pixels square and crisp.
  scale = Math.max(1, Math.floor(Math.min(physW / BASE_W, physH / BASE_H)));
  // Prefer the closer zoom when it still leaves room for the interface: 720p gets 3x, not 2x.
  if (physW / (scale + 1) >= MIN_W && physH / (scale + 1) >= MIN_H) scale += 1;
  // Small windows: stay at 2x if the interface can fit at all, rather than tiny 1x sprites.
  else if (scale === 1 && physW / 2 >= 380 && physH / 2 >= 216) scale = 2;
  // Heroes are drawn at twice the pixel density, so an even zoom keeps their pixels square too.
  // 3x stays: dropping to 2x would make the interface too small on a 720p window.
  if (scale % 2 === 1 && scale >= 5) scale -= 1;
  W = Math.ceil(physW / scale);
  H = Math.ceil(physH / scale);
  // The canvas holds two pixels per logical pixel; the frame loop scales everything up by two,
  // and heroes draw at full density into the same footprint.
  canvas.width = W * 2;
  canvas.height = H * 2;
  canvas.style.width = `${(W * scale) / dpr}px`;
  canvas.style.height = `${(H * scale) / dpr}px`;
  ctx.imageSmoothingEnabled = false;
}

window.addEventListener('resize', resize);
resize();

input.attach(canvas, (cx, cy) => {
  const r = canvas.getBoundingClientRect();
  return [Math.floor(((cx - r.left) / r.width) * W), Math.floor(((cy - r.top) / r.height) * H)];
});
input.onGesture = () => {
  audio.unlock();
  session?.voice.resumePlayback();
};

interface Session {
  socket: GameSocket;
  state: GameState;
  world: WorldRenderer;
  hud: Hud;
  controls: Controls;
  voice: VoiceClient;
  pingTimer: ReturnType<typeof setInterval>;
  visit: RoomVisit;
}

/** How a visit to a room went on this device. The room itself reports what the player did. */
interface RoomVisit {
  /** So that one visit is only ever reported as ended once. */
  leftTracked?: boolean;
  startedAt: number;
  enteredAt: number;
  frames: number;
  pingTotal: number;
  pings: number;
  reconnects: number;
}

let session: Session | null = null;

const host: ScreenHost = {
  inRoom: () => session !== null,
  enterRoom(code: string, hero: HeroSummary) {
    leaveRoom('switched_room');
    const visit: RoomVisit = { startedAt: performance.now(), enteredAt: 0, frames: 0, pingTotal: 0, pings: 0, reconnects: 0 };
    const socket = new GameSocket(code, hero.id, {
      onMessage: (m: S2C) => {
        if (m.t === 'welcome' && !visit.enteredAt) {
          visit.enteredAt = performance.now();
          analytics.track('room_entered', { wait_ms: Math.round(visit.enteredAt - visit.startedAt), class: hero.cls, level: hero.level });
        }
        state.onMessage(m);
        voice.onMessage(m);
      },
      onStatus: (status) => {
        if (status === 'closed') visit.reconnects++;
        state.connected = status === 'open' && state.connected;
        state.statusText = status === 'closed' || status === 'connecting' ? 'Reconnecting...' : 'Joining...';
      },
      onFatal: (message, code) => {
        leaveRoom('connection_lost', { code });
        audio.setMusic(null);
        screens.show('heroes', message);
      },
      onSignedOut: () => {
        leaveRoom('signed_out');
        audio.setMusic(null);
        screens.signedOut(code);
      },
      onRoomClosed: () => {
        trackRoomLeft('room_update');
        // Reloading picks up the new version of the client. The hero list then offers the way back in.
        const u = new URL(location.href);
        u.searchParams.delete('room');
        u.searchParams.set('updated', code);
        location.replace(u);
      },
    });
    const state = new GameState((m: C2S) => socket.send(m));
    state.statusText = 'Joining...';
    const voice = new VoiceClient(code, hero.id, (text, color) => state.pushChat('', text, color));
    const world = new WorldRenderer();
    const hud = new Hud(state, world, voice);
    hud.onLeave = () => {
      leaveRoom('left');
      audio.setMusic(null);
      screens.show('heroes');
    };
    const controls = new Controls(state, world, hud);
    const pingTimer = setInterval(() => {
      if (state.ping > 0) {
        visit.pingTotal += state.ping;
        visit.pings++;
      }
      socket.send({ t: 'ping', c: performance.now() });
    }, 3000);
    session = { socket, state, world, hud, controls, voice, pingTimer, visit };
    socket.connect();
    // Let others join with a plain link.
    try {
      const u = new URL(location.href);
      u.searchParams.set('room', code);
      history.replaceState(null, '', u);
    } catch {
      /* cosmetic */
    }
    (window as unknown as { __warp: unknown }).__warp = session;
  },
};

const screens = new Screens(host);
ui.focusId = null;

void api.config().then((config) => analytics.init(config.analytics)).catch(() => analytics.disable());

function roomLeftProps(reason: string, extra: Record<string, unknown>): Record<string, unknown> {
  const v = session!.visit;
  const seconds = v.enteredAt ? (performance.now() - v.enteredAt) / 1000 : 0;
  return {
    reason,
    seconds: Math.round(seconds),
    never_entered: !v.enteredAt,
    avg_fps: seconds > 0 ? Math.round(v.frames / seconds) : undefined,
    avg_ping_ms: v.pings ? Math.round(v.pingTotal / v.pings) : undefined,
    reconnects: v.reconnects,
    ...extra,
  };
}

function trackRoomLeft(reason: string, extra: Record<string, unknown> = {}): void {
  // An update reloads the page straight after saying why it left, which then looks like the page closing.
  if (!session || session.visit.leftTracked) return;
  session.visit.leftTracked = true;
  analytics.track('room_left', roomLeftProps(reason, extra));
}

// Closing the tab is how most visits end, and a normal send wouldn't get out in time.
window.addEventListener('pagehide', () => {
  if (!session || session.visit.leftTracked) return;
  session.visit.leftTracked = true;
  analytics.trackOnExit('room_left', roomLeftProps('page_closed', {}));
});

window.addEventListener('error', (e) => analytics.exception(e.error ?? e.message, { where: 'window' }));
window.addEventListener('unhandledrejection', (e) => analytics.exception(e.reason, { where: 'promise' }));

function leaveRoom(reason: string, extra: Record<string, unknown> = {}): void {
  if (!session) return;
  trackRoomLeft(reason, extra);
  clearInterval(session.pingTimer);
  session.voice.destroy();
  session.socket.close();
  session = null;
  (window as unknown as { __warp: unknown }).__warp = null;
  input.textMode = false;
  try {
    const u = new URL(location.href);
    u.searchParams.delete('room');
    history.replaceState(null, '', u);
  } catch {
    /* cosmetic */
  }
}

let last = performance.now();
function frame(now: number): void {
  requestAnimationFrame(frame);
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  try {
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    ui.begin(ctx, W, H);
    ctx.imageSmoothingEnabled = false;
    if (session && session.state.map) {
      const { state, world, hud, controls } = session;
      session.visit.frames++;
      state.update(dt);
      const showNames = input.down.has('AltLeft') || input.down.has('AltRight');
      world.draw(ctx, state, W, H, input.mx, input.my, showNames, dt);
      hud.draw(W, H);
      controls.update();
      if (input.pressed.has('Backquote')) {
        debug = !debug;
        if (debug) {
          void api.roomInfo(state.roomCode).then((r) => {
            debugRoom = `room in ${r.colo || '?'}  version ${r.version?.slice(0, 8) ?? '?'}`;
          }).catch(() => undefined);
        }
      }
      if (debug) drawDebug(now);
    } else if (session) {
      ctx.fillStyle = C.bg;
      ctx.fillRect(0, 0, W, H);
      ui.text(session.state.statusText || 'Joining...', W >> 1, H >> 1, C.goldL, { align: 'center' });
    } else {
      screens.draw(W, H, dt);
    }
    if (input.pressed.has('KeyM') && !session) audio.toggleMute();
    drawCursor();
    input.endFrame();
  } catch (err) {
    console.error(err);
    analytics.exception(err, { where: 'frame' });
    input.endFrame();
  }
}

// A chunky gauntlet-style pointer, tinted by what it would do on click.
const CURSOR = [
  'kk.......',
  'kwk......',
  'kwwk.....',
  'kwwwk....',
  'kwwwwk...',
  'kwwwwwk..',
  'kwwwwwwk.',
  'kwwwwkkkk',
  'kwkwwk...',
  'kk.kwwk..',
  '....kwk..',
  '.....k...',
];

function drawCursor(): void {
  // A DOM panel is on top, with the browser's own pointer.
  if (input.blocked) return;
  const hover = session?.world;
  const myTeam = session ? session.state.units.get(session.state.myUid)?.team ?? 1 : 1;
  let fill = '#f8d878';
  if (session && !ui.captured && hover) {
    if (hover.hoverUnit) fill = hover.hoverUnit.team !== myTeam && hover.hoverUnit.hp > 0 ? '#f83800' : '#58f898';
    else if (hover.hoverObject || hover.hoverItem) fill = '#3cbcfc';
  }
  for (let y = 0; y < CURSOR.length; y++) {
    const row = CURSOR[y];
    for (let x = 0; x < row.length; x++) {
      const c = row[x];
      if (c === '.') continue;
      ctx.fillStyle = c === 'k' ? '#000000' : fill;
      ctx.fillRect(input.mx + x, input.my + y, 1, 1);
    }
  }
}

let debug = false;
let debugRoom = '';
const fpsHistory: number[] = [];
function drawDebug(now: number): void {
  fpsHistory.push(now);
  while (fpsHistory.length && now - fpsHistory[0] > 1000) fpsHistory.shift();
  const s = session!.state;
  const n = s.netStats;
  ui.text(debugRoom, 4, H - 62, C.green);
  ui.text(`fps ${fpsHistory.length}  units ${s.units.size}  items ${s.items.size}  fx ${s.particles.length}  ${W}x${H} @${scale}x`, 4, H - 53, C.green);
  ui.text(`net fixes ${n.corrections}/${n.samples}  max err ${n.maxErr.toFixed(2)}`, 4, H - 44, C.green);
}

requestAnimationFrame(frame);

// Exposed for automated testing & debugging.
(window as unknown as { __warpInput: unknown }).__warpInput = input;
(window as unknown as { __warpData: unknown }).__warpData = { CLASSES, SKILLS, REALMS, MONSTERS, monsterSprite, heroFrame };
